import isFunction from 'lodash-es/isFunction';
import { type ForwardedRef, forwardRef, useMemo } from 'react';

import { ContractType } from '@/common/graphql';
import { ContractTypeLabels } from '@/common/types';
import { ObjectUtils } from '@/common/utils';

import type { ComboboxItemWithFormatted } from '../props';
import { SelectMultiple, type SelectMultipleProps } from '../SelectMultiple';
import { SelectSingle, type SelectSingleProps } from '../SelectSingle';

interface ContractTypeSelectSingleProps
  extends Omit<SelectSingleProps<ComboboxItemWithFormatted<ContractType>>, 'searchable' | 'value'> {
  value: ContractType | null;
}

interface ContractTypeSelectMultipleProps
  extends Omit<SelectMultipleProps<ComboboxItemWithFormatted<ContractType>>, 'searchable' | 'value'> {
  value: Array<ContractType> | null | undefined;
}

const sortOrder = [
  ContractType.FtPermanent,
  ContractType.FtFixedTerm,
  ContractType.FtFreelancer,
  ContractType.FtConsultant,
  ContractType.PartTime,
  ContractType.Intern,
  ContractType.Contractor,
];

const data = ObjectUtils.arrayFromEnumValues<ContractType>(ContractType)
  .sort((a, b) => sortOrder.indexOf(a) - sortOrder.indexOf(b))
  .map(value => ({ label: ContractTypeLabels[value], value }));

export const ContractTypeSelectSingle = forwardRef(
  (
    { filter, placeholder = 'Select Contract Type', ...rest }: ContractTypeSelectSingleProps,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const filtered = useMemo(() => {
      if (isFunction(filter)) return data.filter(filter);
      else return data;
    }, [filter]);

    return <SelectSingle ref={ref} {...rest} data={filtered} loading={false} placeholder={placeholder} />;
  },
);

export const ContractTypeSelectMultiple = ({
  placeholder = 'Select Contract Type',
  ...props
}: ContractTypeSelectMultipleProps) => (
  <SelectMultiple
    {...props}
    data={data.filter(props.filter ?? (() => true))}
    loading={false}
    maxItemVisible={data.length}
    multipleSelectedLabel={length => `${length} contract types`}
    placeholder={placeholder}
    searchable={false}
  />
);
